import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Drawer, IconButton, Portal, Tooltip, Typography } from '@mui/material';
import { CancelIcon, ChatIcon } from '@sharedCR/coreDesignSystem/icons';
import MilestoneStatusCellRenderer from 'app/pages/loanPipelines/LoanPipelinesTable/common/LoanPipelinesCellRenderer/MilestoneStatusCellRenderer';
import { TeamChatConnector } from 'app/shared/components/TeamChat';
import { getChannelLoanHeader } from 'app/shared/components/TeamChat/TeamChat.utils';
import SecondaryTabGroupCDS from 'app/shared/componentsRestyled/coreDesignSystem/SecondaryTabGroupCDS';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { IconSize } from 'constants/coreDesignSystem';
import { TagType } from 'constants/tag';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  getUuid,
  selectIsClaimAllowed,
  selectIsSubCompanyUser,
  selectUserData,
} from 'store/auth/selectors';
import {
  selectCommonInfoLoanApplication,
  selectExtendedLoanRolesData,
  selectLoanStatusHeaderValue,
  selectLoanSubCompanyId,
  selectPrimaryBorrowerLastName,
  selectSubjectProperty,
} from 'store/loanApplications/selectors';
import { getMilestonePresentationSettingsAction } from 'store/milestoneDefinitions/actions';
import { fetchChannelsAction } from 'store/teamChat/actions';
import {
  selectPendingEntityChatAttachment,
  selectPendingEntityChatFilter,
  selectTeamChatChannel,
} from 'store/teamChat/selectors';

import { getChatName } from '../../../tabs/activities/Activities.utils';
import {
  DRAWER_FALLBACK_TITLE,
  DRAWER_MODAL_PROPS,
  LOAN_CHAT_UNREAD_BADGE_MAX_DISPLAY,
  LoanEditorChatChannelKey,
} from '../../LoanEditorChatDrawer.constants';
import {
  bodyStyles,
  channelSwitcherRowStyles,
  drawerHeaderStyles,
  drawerPaperStyles,
  drawerRootStyles,
  edgeTriggerIconStyles,
  edgeTriggerStyles,
  headerActionsStyles,
  loanBadgeStyles,
  resizeHandleStyles,
  subtitleRowStyles,
  titleGroupStyles,
  titleStyles,
  triggerBadgeLabelStyles,
  triggerBadgeStyles,
} from '../../LoanEditorChatDrawer.styles';
import {
  buildKnownMemberNamesByUserId,
  buildLoanEditorChatChannels,
  clampDrawerWidth,
  formatUnreadCountLabel,
  getChannelKeyForChatId,
  getInternalChatId,
  getTotalUnreadCount,
  getTpoChatId,
  readStoredOpenState,
  readStoredWidth,
  writeStoredOpenState,
  writeStoredWidth,
} from '../../LoanEditorChatDrawer.utils';
import EscalateTpoChatControl from '../EscalateTpoChatControl';
import LoanEditorChatChannelPanel from '../LoanEditorChatChannelPanel';

import { LoanEditorChatDrawerInnerTestIds } from './LoanEditorChatDrawerInner.testIds';

const LoanEditorChatDrawerInner = memo(({ applicationId }) => {
  const dispatch = useDispatch();

  const loggedUserId = useSelector(getUuid);
  const currentUserData = useSelector(selectUserData);
  const subjectProperty = useSelector(selectSubjectProperty);
  const primaryBorrowerLastName = useSelector(selectPrimaryBorrowerLastName);
  const loanStatusValue = useSelector(selectLoanStatusHeaderValue);
  const commonInfo = useSelector(selectCommonInfoLoanApplication);
  const extendedLoanRoles = useSelector(selectExtendedLoanRolesData);
  const loanSubCompanyId = useSelector(selectLoanSubCompanyId);

  const internalChatId = useMemo(() => getInternalChatId(applicationId), [applicationId]);
  const tpoChatId = useMemo(() => getTpoChatId(applicationId), [applicationId]);

  const internalChannel = useSelector(selectTeamChatChannel(internalChatId));
  const tpoChannel = useSelector(selectTeamChatChannel(tpoChatId));
  const pendingEntityAttachment = useSelector(selectPendingEntityChatAttachment);
  const pendingEntityChatFilter = useSelector(selectPendingEntityChatFilter);

  const isSubCompanyUser = useSelector(selectIsSubCompanyUser);

  const hasTpoViewClaim = useSelector(selectIsClaimAllowed(COMPOSED_CLAIMS.TPO_CHAT_VIEW));
  const isTpoParticipant = !!tpoChannel;
  const isTpoChannelAvailable = hasTpoViewClaim || isTpoParticipant;
  const isInternalChannelAvailable = !isSubCompanyUser;

  const companyId = useCompanyId();
  const loanRoleUserIds = useMemo(
    () =>
      (extendedLoanRoles ?? [])
        .map(role => role?.pendingChange?.newUserId || role?.userId)
        .filter(Boolean),
    [extendedLoanRoles],
  );
  const knownMemberNamesByUserId = useMemo(
    () =>
      buildKnownMemberNamesByUserId({
        extendedLoanRoles,
        currentUserId: loggedUserId,
        currentUserData,
      }),
    [extendedLoanRoles, loggedUserId, currentUserData],
  );

  const chatName = getChatName(subjectProperty, primaryBorrowerLastName);

  const channels = useMemo(
    () =>
      buildLoanEditorChatChannels({
        internalChatId,
        tpoChatId,
        isTpoChannelAvailable,
        isInternalChannelAvailable,
      }),
    [internalChatId, tpoChatId, isTpoChannelAvailable, isInternalChannelAvailable],
  );

  const [isOpen, setIsOpen] = useState(readStoredOpenState);
  const [width, setWidth] = useState(readStoredWidth);
  const [activeChannelKey, setActiveChannelKey] = useState(
    () => channels[0]?.key ?? LoanEditorChatChannelKey.INTERNAL,
  );

  const unreadByChannelKey = useMemo(
    () => ({
      [LoanEditorChatChannelKey.INTERNAL]: internalChannel?.unreadMessageCount ?? 0,
      [LoanEditorChatChannelKey.TPO]: tpoChannel?.unreadMessageCount ?? 0,
    }),
    [internalChannel, tpoChannel],
  );

  const channelSwitcherButtons = useMemo(
    () =>
      channels.map(({ key, label }) => {
        const unreadCount = unreadByChannelKey[key] ?? 0;

        return {
          value: key,
          label,
          badge:
            unreadCount > 0
              ? formatUnreadCountLabel(unreadCount, LOAN_CHAT_UNREAD_BADGE_MAX_DISPLAY)
              : null,
        };
      }),
    [channels, unreadByChannelKey],
  );

  const totalUnreadCount = useMemo(
    () => getTotalUnreadCount(channels, unreadByChannelKey),
    [channels, unreadByChannelKey],
  );

  const channelLoanHeader = getChannelLoanHeader(internalChannel);
  const loanNumber = commonInfo?.loanNumber || channelLoanHeader?.loanNumber || null;
  const milestoneStatusText = loanStatusValue || channelLoanHeader?.milestoneStatusText || null;
  const milestoneValue = useMemo(
    () => (milestoneStatusText ? { name: milestoneStatusText } : null),
    [milestoneStatusText],
  );

  const isTpoChannelActive = activeChannelKey === LoanEditorChatChannelKey.TPO;

  const resizeStateRef = useRef(null);

  const onToggleOpen = useCallback(() => {
    setIsOpen(previous => {
      const next = !previous;

      writeStoredOpenState(next);

      return next;
    });
  }, []);

  const onClose = useCallback(() => {
    setIsOpen(false);
    writeStoredOpenState(false);
  }, []);

  const onResizeMove = useCallback(e => {
    const state = resizeStateRef.current;

    if (!state) {
      return;
    }

    const delta = state.startX - e.clientX;
    const nextWidth = clampDrawerWidth(state.startWidth + delta);

    state.latestWidth = nextWidth;
    setWidth(nextWidth);
  }, []);

  const onResizeEnd = useCallback(() => {
    const state = resizeStateRef.current;

    if (!state) {
      return;
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('mousemove', onResizeMove);
      window.removeEventListener('mouseup', onResizeEnd);
    }

    resizeStateRef.current = null;
    writeStoredWidth(state.latestWidth);
  }, [onResizeMove]);

  const onResizeStart = useCallback(
    e => {
      e.preventDefault();

      resizeStateRef.current = {
        startX: e.clientX,
        startWidth: width,
        latestWidth: width,
      };

      if (typeof window !== 'undefined') {
        window.addEventListener('mousemove', onResizeMove);
        window.addEventListener('mouseup', onResizeEnd);
      }
    },
    [width, onResizeMove, onResizeEnd],
  );

  const paperProps = useMemo(() => ({ sx: drawerPaperStyles(width) }), [width]);
  const triggerSx = useMemo(() => edgeTriggerStyles(isOpen), [isOpen]);

  useEffectSafeStrictMode(() => {
    if (!loggedUserId) {
      return;
    }

    dispatch(fetchChannelsAction());
    dispatch(getMilestonePresentationSettingsAction());
  }, [dispatch, loggedUserId]);

  useEffect(() => {
    const isActiveChannelAvailable = channels.some(({ key }) => key === activeChannelKey);

    if (!isActiveChannelAvailable && channels.length > 0) {
      setActiveChannelKey(channels[0].key);
    }
  }, [channels, activeChannelKey]);

  // "Discuss in chat" (DEV-5448/DEV-5591) — a Financial Info / Borrowers row action queues this
  // intent from OUTSIDE the drawer's subtree. The drawer owns its own open/active-channel state
  // locally (not in Redux), so it has to react here rather than the intent driving the state
  // directly; the composer (useChatEntityAttachment) separately seeds the token once its channel is
  // active. Switch to whichever channel the intent's chatId addresses (internal or broker/TPO).
  useEffect(() => {
    const targetChannelKey = pendingEntityAttachment
      ? getChannelKeyForChatId(pendingEntityAttachment.chatId, { internalChatId, tpoChatId })
      : null;

    if (!targetChannelKey) {
      return;
    }

    setIsOpen(true);
    writeStoredOpenState(true);
    setActiveChannelKey(targetChannelKey);
  }, [pendingEntityAttachment, internalChatId, tpoChatId]);

  // Reference-badge click (DEV-5446) — the counterpart of the "Discuss in chat" effect above, for
  // a queued `pendingEntityChatFilter` intent instead. Only opens + switches channel; TeamChatChannel
  // (mounted once the internal panel activates) separately applies + clears the filter itself via
  // useChatEntityFilter.
  useEffect(() => {
    if (!pendingEntityChatFilter || pendingEntityChatFilter.chatId !== internalChatId) {
      return;
    }

    setIsOpen(true);
    writeStoredOpenState(true);
    setActiveChannelKey(LoanEditorChatChannelKey.INTERNAL);
  }, [pendingEntityChatFilter, internalChatId]);

  useEffect(
    () => () => {
      if (typeof window === 'undefined' || !resizeStateRef.current) {
        return;
      }

      window.removeEventListener('mousemove', onResizeMove);
      window.removeEventListener('mouseup', onResizeEnd);
    },
    [onResizeMove, onResizeEnd],
  );

  return (
    <>
      <Tooltip placement="left" title="Open loan chat">
        <IconButton
          disableRipple
          aria-label="Open loan chat"
          data-testid={LoanEditorChatDrawerInnerTestIds.OPEN_TRIGGER_BUTTON}
          sx={triggerSx}
          onClick={onToggleOpen}
        >
          <Box sx={edgeTriggerIconStyles}>
            <ChatIcon />
          </Box>
          {!isOpen && totalUnreadCount > 0 && (
            <Box
              aria-label={`${totalUnreadCount} unread chat messages`}
              data-testid={LoanEditorChatDrawerInnerTestIds.UNREAD_BADGE}
              sx={triggerBadgeStyles}
            >
              <Typography sx={triggerBadgeLabelStyles} variant="bold12">
                {formatUnreadCountLabel(totalUnreadCount, LOAN_CHAT_UNREAD_BADGE_MAX_DISPLAY)}
              </Typography>
            </Box>
          )}
        </IconButton>
      </Tooltip>
      <Portal>
        <Drawer
          ModalProps={DRAWER_MODAL_PROPS}
          PaperProps={paperProps}
          anchor="right"
          data-testid={LoanEditorChatDrawerInnerTestIds.DRAWER}
          open={isOpen}
          sx={drawerRootStyles}
          variant="persistent"
          onClose={onClose}
        >
          <Box
            aria-label="Resize loan chat drawer"
            role="separator"
            sx={resizeHandleStyles}
            onMouseDown={onResizeStart}
          />
          <Box sx={drawerHeaderStyles}>
            <Box sx={titleGroupStyles}>
              <Typography sx={titleStyles} variant="bold16">
                {chatName || DRAWER_FALLBACK_TITLE}
              </Typography>
              <Box sx={subtitleRowStyles}>
                {!!loanNumber && (
                  <Typography sx={loanBadgeStyles} variant="regular12">
                    {`#${loanNumber}`}
                  </Typography>
                )}
                {!!milestoneValue && (
                  <MilestoneStatusCellRenderer
                    isNameKey
                    type={TagType.SMALL_FAT}
                    value={milestoneValue}
                  />
                )}
              </Box>
            </Box>
            <Box sx={headerActionsStyles}>
              {isTpoChannelActive && <EscalateTpoChatControl chatId={tpoChatId} />}
              <IconButton
                aria-label="Close loan chat drawer"
                data-testid={LoanEditorChatDrawerInnerTestIds.CLOSE_BUTTON}
                size="small"
                onClick={onClose}
              >
                <CancelIcon size={IconSize[20]} />
              </IconButton>
            </Box>
          </Box>
          {channels.length > 1 && (
            <Box sx={channelSwitcherRowStyles}>
              <SecondaryTabGroupCDS
                buttons={channelSwitcherButtons}
                value={activeChannelKey}
                onChange={setActiveChannelKey}
              />
            </Box>
          )}
          <Box sx={bodyStyles}>
            <TeamChatConnector>
              {channels.map(({ key, chatId }) => (
                <LoanEditorChatChannelPanel
                  chatName={chatName}
                  companyId={companyId}
                  currentUserId={loggedUserId}
                  expectedChatId={chatId}
                  isActive={isOpen && activeChannelKey === key}
                  isSubCompanyUser={isSubCompanyUser}
                  key={key}
                  knownMemberNamesByUserId={knownMemberNamesByUserId}
                  priorityMemberIds={loanRoleUserIds}
                  showEntityReferencesRail={key === LoanEditorChatChannelKey.INTERNAL}
                  subCompanyId={key === LoanEditorChatChannelKey.TPO ? loanSubCompanyId : null}
                />
              ))}
            </TeamChatConnector>
          </Box>
        </Drawer>
      </Portal>
    </>
  );
});
LoanEditorChatDrawerInner.propTypes = {
  applicationId: PropTypes.string.isRequired,
};

export default LoanEditorChatDrawerInner;
